import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  GetUsersParams,
  GetUsersResponse,
} from '../interfaces/get-users';
import { RequestError } from '../../../interfaces/error.interface';
import { GetUserResponse } from '../interfaces/get-user';
import { User } from '../../../interfaces/user.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { userMapper } from '../../../mappers/userMapper';

export interface UserState {
  users: {
    ids: string[];
    entities: Record<string, User>;
  };
  user: User | null;
  options: GetUsersParams;
  page: number;
  size: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: UserState = {
  users: {
    ids: [],
    entities: {},
  },
  user: null,
  options: {},
  page: 1,
  size: 100,
  total: 0,
  noMore: false,
  errorCode: null,
};

const userAdapter = createEntityAdapter<User>((user: User) => user.id);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUsersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getUsersSuccessApiAction: (state, action: PayloadAction<GetUsersResponse>) => {
      const users = userMapper.mapResponseToUsers(action.payload);
      userAdapter.addMany(users, state.users);

      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.users.length < state.size;
    },
    getUsersFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getUserInitApiAction: (state) => {
      state.errorCode = null;
      state.user = null;
    },
    getUserSuccessApiAction: (state, action: PayloadAction<GetUserResponse>) => {
      state.user = userMapper.mapResponseToUser(action.payload.user);
    },
    getUserFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postUserInitApiAction: (state) => {
      state.errorCode = null;
    },
    postUserSuccessApiAction: () => {},
    postUserFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putUserInitApiAction: (state) => {
      state.errorCode = null;
    },
    putUserSuccessApiAction: () => {},
    putUserFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteUserInitApiAction: () => {},
    deleteUserSuccessApiAction: () => {},
    deleteUserFailuireApiAction: () => {},
    setUserAction: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setOptionsAction: (state, action: PayloadAction<GetUsersParams>) => {
      state.options = action.payload;
      userAdapter.removeAll(state.users);
      state.page = 1;
    },
    resetOptionsAction: (state) => {
      state.options = {};
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    userClearAction: (state) => {
      state.user = null;
      state.errorCode = null;
    },
  },
});

export const {
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  getUsersFailureApiAction,
  getUserInitApiAction,
  getUserSuccessApiAction,
  getUserFailureApiAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  postUserFailuireApiAction,
  putUserInitApiAction,
  putUserSuccessApiAction,
  putUserFailuireApiAction,
  deleteUserInitApiAction,
  deleteUserSuccessApiAction,
  deleteUserFailuireApiAction,
  setUserAction,
  increasePageAction,
  resetPageAction,
  setOptionsAction,
  resetOptionsAction,
  userClearAction,
} = userSlice.actions;

export default userSlice.reducer;
